import { TENANT_HEALTH_STATUS } from '@experiences/interfaces';

export const UNIT_TYPE_QUERY_PARAM = 'unitType';
export const START_DATE_QUERY_PARAM = 'startDate';
export const END_DATE_QUERY_PARAM = 'endDate';
export const FOLDER_KEY_QUERY_PARAM = 'folderKey';

export const VALID_UNIT_TYPES = [ 'AIU', 'AGU', 'PLTU', 'HEAL', 'HEALTEST', 'SPR', 'RU', 'TEU', 'GIC' ] as const;

export const USER_TOPUP_UNIT_TYPES: readonly string[] = [ 'PLTU', 'AGU' ];

// Not every enum value: NONE tenants are reachable only through "All", CRITICAL is never produced.
export const TENANT_STATUS_VALUES = [
    TENANT_HEALTH_STATUS.WARNING,
    TENANT_HEALTH_STATUS.HEALTHY,
    TENANT_HEALTH_STATUS.DISABLED,
    TENANT_HEALTH_STATUS.DELETED,
] as const;

export type FilterableTenantHealthStatus = typeof TENANT_STATUS_VALUES[number];

export const PROCESS_TYPE_TRANSLATION_KEYS: Record<string, string> = {
    Process: 'CLIENT_CONSUMPTION_DASHBOARD_PROCESS_TYPE_PROCESS',
    ProcessOrchestration: 'CLIENT_CONSUMPTION_DASHBOARD_PROCESS_TYPE_PROCESS_ORCHESTRATION',
    WebApp: 'CLIENT_CONSUMPTION_DASHBOARD_PROCESS_TYPE_WEB_APP',
    Agent: 'CLIENT_CONSUMPTION_DASHBOARD_PROCESS_TYPE_AGENT',
    TestAutomationProcess: 'CLIENT_CONSUMPTION_DASHBOARD_PROCESS_TYPE_TEST_AUTOMATION',
    Api: 'CLIENT_CONSUMPTION_DASHBOARD_PROCESS_TYPE_API',
    MCPServer: 'CLIENT_CONSUMPTION_DASHBOARD_PROCESS_TYPE_MCP_SERVER',
    BusinessRules: 'CLIENT_CONSUMPTION_DASHBOARD_PROCESS_TYPE_BUSINESS_RULES',
    CaseManagement: 'CLIENT_CONSUMPTION_DASHBOARD_PROCESS_TYPE_CASE_MANAGEMENT',
    Flow: 'CLIENT_CONSUMPTION_DASHBOARD_PROCESS_TYPE_FLOW',
    Function: 'CLIENT_CONSUMPTION_DASHBOARD_PROCESS_TYPE_FUNCTION',
};

export const PROCESS_TYPES = Object.fromEntries(
    Object.keys(PROCESS_TYPE_TRANSLATION_KEYS).map(key => [ key, key ]),
) as { [K in keyof typeof PROCESS_TYPE_TRANSLATION_KEYS]: K };

export function getProcessTypeDisplayName(
    processType: string,
    translate: (descriptor: { id: string }) => string,
): string {
    const key = PROCESS_TYPE_TRANSLATION_KEYS[processType];
    return key ? translate({ id: key }) : processType;
}

/**
 * Get translated labels for tenant health statuses
 * @param translate - The formatMessage function from useIntl
 * @returns Record mapping status values to translated labels
 */
export function getTenantStatusLabels(
    translate: (message: { id: string }) => string,
): Record<FilterableTenantHealthStatus, string> {
    return {
        [TENANT_HEALTH_STATUS.WARNING]: translate({ id: 'CLIENT_CONSUMPTION_DASHBOARD_STATUS_WARNING' }),
        [TENANT_HEALTH_STATUS.HEALTHY]: translate({ id: 'CLIENT_CONSUMPTION_DASHBOARD_STATUS_HEALTHY' }),
        [TENANT_HEALTH_STATUS.DISABLED]: translate({ id: 'CLIENT_CONSUMPTION_DASHBOARD_STATUS_DISABLED' }),
        [TENANT_HEALTH_STATUS.DELETED]: translate({ id: 'CLIENT_CONSUMPTION_DASHBOARD_STATUS_DELETED' }),
    };
}

export function getUnitTypeDisplayName(
    productCode: string,
    translate: (message: { id: string }) => string,
): string {
    const PRODUCT_CODE_TO_TRANSLATION_KEY: Record<string, string> = {
        AIU: 'CLIENT_AIU',
        AGU: 'CLIENT_AGU',
        PLTU: 'CLIENT_PLTU',
        HEAL: 'CLIENT_HEAL',
        HEALTEST: 'CLIENT_HEALTEST',
        SPR: 'CLIENT_SPR',
        RU: 'CLIENT_RU',
        TEU: 'CLIENT_TEU',
        GIC: 'CLIENT_GIC',
    };
    const translationKey = PRODUCT_CODE_TO_TRANSLATION_KEY[productCode];
    return translationKey ? translate({ id: translationKey }) : productCode;
}

/**
 * Build a consumption dashboard URL with unitType and optional date range params.
 * Date params are only included when the range differs from the bundle interval.
 */
export function buildConsumptionDashboardUrl(
    baseRoute: string,
    unitType: string,
    startDate?: string,
    endDate?: string,
    bundleStartDate?: string,
    bundleEndDate?: string,
    folderKey?: string,
): string {
    const params = new URLSearchParams();
    params.set(UNIT_TYPE_QUERY_PARAM, unitType);

    const hasCustomRange = startDate && endDate
        && (startDate !== bundleStartDate || endDate !== bundleEndDate);

    if (hasCustomRange) {
        params.set(START_DATE_QUERY_PARAM, startDate);
        params.set(END_DATE_QUERY_PARAM, endDate);
    }

    if (folderKey) {
        params.set(FOLDER_KEY_QUERY_PARAM, folderKey);
    }

    return `${baseRoute}?${params.toString()}`;
}

/**
 * Format a range value — "YYYY-MM-DD" or a wall-clock "YYYY-MM-DDTHH:mm:ss" in the display
 * zone — to a short display format (e.g., "Mar 1, 2024" / "Mar 1, 2024, 10:00 PM").
 */
export function formatDisplayDate(dateStr: string, locale: string): string {
    const normalizedLocale = locale === 'en' || locale?.toLowerCase() === 'keys' ? 'en-US' : locale;
    const hasTime = dateStr.includes('T');
    // wall-clock values carry no offset; parsing as UTC and rendering in UTC shows them verbatim
    const date = new Date(hasTime ? `${dateStr}Z` : `${dateStr}T00:00:00Z`);
    return date.toLocaleString(normalizedLocale, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        ...(hasTime ? {
            hour: 'numeric',
            minute: '2-digit',
        } : {}),
        timeZone: 'UTC',
    });
}

/**
 * Format a consumption number to 2 decimal places with locale-aware thousands separators.
 */
export function formatConsumptionValue(value: number): string {
    return value.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}

/**
 * Calculate a percentage with 2 decimal places.
 */
export function calcPercent(value: number, total: number): number {
    return total > 0
        ? Math.round((value / total) * 10000) / 100
        : 0;
}

/**
 * Split user top-up consumption out of the org-pool segment for a display-only breakdown.
 * Top-ups are a subset of the org pool, so the value is clamped to [0, consumedFromOrgPool]
 * to guard against inconsistent backend data (negative, or larger than the org pool).
 */
export function computeTopUpSplit(
    consumedFromOrgPool: number,
    rawTopUp: number,
): { topUp: number; consumedFromOrgPoolDisplay: number } {
    const topUp = Math.min(Math.max(0, rawTopUp), Math.max(0, consumedFromOrgPool));
    return {
        topUp,
        consumedFromOrgPoolDisplay: Math.max(0, consumedFromOrgPool - topUp),
    };
}

/**
 * Width (as a percentage of `total`) of a capacity-breakdown bar segment. A non-zero value is
 * floored at 0.5% so a tiny slice stays visible; an empty segment returns 0.
 */
export function visualSegmentPercent(value: number, total: number): number {
    return value > 0 ? Math.max(0.5, calcPercent(value, total)) : 0;
}

/**
 * Format a percentage value to 2 decimal places (e.g. "12.50%").
 */
export function formatPercent(value: number): string {
    return `${value.toFixed(2)}%`;
}

/**
 * Format Unix timestamp (seconds) to a full date + time in the given zone.
 * @param timestamp - Unix timestamp in seconds
 * @param locale - Locale string (e.g., 'en', 'de')
 * @param timeZoneId - IANA display zone; defaults to UTC
 * @returns Formatted string (e.g., "1 January 2025, 09:00 AM")
 */
function formatDayMonthYearTime(timestamp: number, locale: string, timeZoneId?: string): string {
    const date = new Date(timestamp * 1000);
    const normalizedLocale = locale === 'en' || locale?.toLowerCase() === 'keys' ? 'en-US' : locale;
    return date.toLocaleString(normalizedLocale, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZone: timeZoneId ?? 'UTC',
    });
}

/**
 * Format period from start and end timestamps, with time, in the display zone.
 * @param startDate - Unix timestamp in seconds
 * @param endDate - Unix timestamp in seconds
 * @param locale - Locale string (e.g., 'en', 'de')
 * @param timeZoneId - IANA display zone; defaults to UTC
 * @returns Formatted period string (e.g., "1 January 2025, 9:00 AM - 31 December 2025, 8:59 AM")
 */
export function formatPeriod(startDate: number, endDate: number, locale: string, timeZoneId?: string): string {
    return `${formatDayMonthYearTime(startDate, locale, timeZoneId)} - ${formatDayMonthYearTime(endDate, locale, timeZoneId)}`;
}

/**
 * Calculate days remaining from Unix timestamp (seconds) to now
 * @param endDate - Unix timestamp in seconds
 * @returns Number of days remaining (can be negative if period has ended)
 */
export function calculateDaysRemaining(endDate: number): number {
    const endDateMs = endDate * 1000; // Convert seconds to milliseconds
    const now = Date.now();
    const diffMs = endDateMs - now;
    return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
}

/**
 * Constants for process detail redirect URLs.
 */
const PROCESS_VIEW_CONFIG = {
    servicePaths: {
        orchestrator: 'orchestrator_/processes',
        maestro: 'maestro_/processes',
    },
    queryParams: {
        orchestratorFolderKey: 'fkey',
        orchestratorSearch: 'search',
        maestroFolderKey: 'folderKey',
    },
} as const;

/**
 * Build a URL to view a process in its service.
 * Opens orchestrator for most process types, maestro for agentic processes.
 */
export function buildProcessViewUrl(
    orgLogicalName: string,
    tenantId: string,
    processType: string,
    folderKey: string,
    processName?: string,
    processKey?: string,
): string | null {
    const {
        servicePaths, queryParams,
    } = PROCESS_VIEW_CONFIG;
    const baseUrl = window.location.origin;

    if (processType === PROCESS_TYPES.ProcessOrchestration) {
        if (!processKey) {
            return null;
        }
        const params = new URLSearchParams();
        params.set(queryParams.maestroFolderKey, folderKey);
        return `${baseUrl}/${orgLogicalName}/${tenantId}/${servicePaths.maestro}/${encodeURIComponent(processKey)}?${params.toString()}`;
    }

    const params = new URLSearchParams();
    params.set(queryParams.orchestratorFolderKey, folderKey);
    if (processName) {
        params.set(queryParams.orchestratorSearch, processName);
    }
    return `${baseUrl}/${orgLogicalName}/${tenantId}/${servicePaths.orchestrator}?${params.toString()}`;
}
